import { z } from "zod/v3";

export * from "./contexts/currency-context-provider";
export * from "./contexts/currency-context";

export const Denomination = z.object({
	code: z.string(),
	value: z.number(),
	name: z.string(),
	type: z.enum(["Coin", "Note"]),
	active: z.boolean(),
	color: z.string().optional(),
	image: z.string().nullable().optional(),
});

export const Currency = z.object({
	code: z.string(),
	name: z.string(),
	symbol: z.string(),
	isPrimary: z.boolean(),
	decimalPlaces: z.number(),
	active: z.boolean(),
	denominations: z.array(Denomination).optional(),
});

export const RateUpdateMode = z.enum([
	"Manual",
	"AutoUpdate",
	"RequireApproval",
]);

export const ExchangeRateSettings = z.object({
	updateMode: RateUpdateMode.default("Manual"),
	updateFrequency: z.enum(["Daily", "Weekly", "Monthly", "Manual"]),
	lastFullUpdate: z.string().optional().nullable(),
	changeThreshold: z.number().default(0),
	rateAdjustment: z.number().default(0),
});

export const ExchangeRate = z.object({
	id: z.string(),
	fromCurrency: z.string(),
	toCurrency: z.string(),
	rate: z.number(),
	lastUpdated: z.string(),
	settings: ExchangeRateSettings.optional().nullable(),
});

export const PendingRateUpdate = z.object({
	rateId: z.string(),
	proposedRate: z.number(),
	currentRate: z.number(),
	source: z.string(),
	fetchedAt: z.string(),
});

export const ProviderConfigField = z.object({
	key: z.string(),
	label: z.string(),
	required: z.boolean(),
	sensitive: z.boolean().default(false),
});

export const ExchangeRateProviderInfo = z.object({
	key: z.string(),
	displayName: z.string(),
	configurationFields: z.array(ProviderConfigField),
	supportedCurrencies: z.array(z.string()),
});

export const SystemProviderStatus = z.object({
	key: z.string(),
	displayName: z.string(),
	enabled: z.boolean(),
	configurationFields: z.array(ProviderConfigField),
	supportedCurrencyCount: z.number(),
	hasConfiguration: z.boolean(),
});

export const CurrencyData = z.object({
	currencies: z.array(Currency),
	exchangeRates: z.array(ExchangeRate),
	pendingRateUpdates: z.array(PendingRateUpdate).default([]),
});

// HA-928: the display read (GET /settings/currencies, GET /settings/currencies/{venueId}), only what showing money needs.
// Names, symbols and denominations come from the static Currencies list. The full settings (CurrencyData) are for the
// manage screens.
export const DisplayCurrency = z.object({
	code: z.string(),
	active: z.boolean(),
	isPrimary: z.boolean(),
});

export const DisplayRate = z.object({
	fromCurrency: z.string(),
	toCurrency: z.string(),
	rate: z.number(),
});

export const CurrencyDisplay = z.object({
	venueId: z.string(),
	currencies: z.array(DisplayCurrency),
	exchangeRates: z.array(DisplayRate),
});

// Type inference from Zod schemas
export type Currency = z.infer<typeof Currency>;
export type Denomination = z.infer<typeof Denomination>;
export type ExchangeRate = z.infer<typeof ExchangeRate>;
export type ExchangeRateSettings = z.infer<typeof ExchangeRateSettings>;
export type RateUpdateMode = z.infer<typeof RateUpdateMode>;
export type CurrencyData = z.infer<typeof CurrencyData>;
export type DisplayCurrency = z.infer<typeof DisplayCurrency>;
export type DisplayRate = z.infer<typeof DisplayRate>;
export type CurrencyDisplay = z.infer<typeof CurrencyDisplay>;
export type PendingRateUpdate = z.infer<typeof PendingRateUpdate>;
export type ExchangeRateProviderInfo = z.infer<typeof ExchangeRateProviderInfo>;
export type ProviderConfigField = z.infer<typeof ProviderConfigField>;
export type SystemProviderStatus = z.infer<typeof SystemProviderStatus>;

export const Currencies: Record<string, Currency> = {
	USD: {
		code: "USD",
		name: "US Dollar",
		symbol: "$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "USD",
				value: 1,
				name: "Penny",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "USD",
				value: 5,
				name: "Nickel",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 10,
				name: "Dime",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 25,
				name: "Quarter",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 50,
				name: "Half Dollar",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 100,
				name: "Dollar Coin",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "USD",
				value: 100,
				name: "One Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 200,
				name: "Two Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 500,
				name: "Five Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 1000,
				name: "Ten Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 2000,
				name: "Twenty Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 5000,
				name: "Fifty Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 10000,
				name: "Hundred Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
		],
	},
	EUR: {
		code: "EUR",
		name: "Euro",
		symbol: "\u20AC",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "EUR",
				value: 1,
				name: "1 Cent",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "EUR",
				value: 2,
				name: "2 Cent",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "EUR",
				value: 5,
				name: "5 Cent",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "EUR",
				value: 10,
				name: "10 Cent",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "EUR",
				value: 20,
				name: "20 Cent",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "EUR",
				value: 50,
				name: "50 Cent",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "EUR",
				value: 100,
				name: "1 Euro",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "EUR",
				value: 200,
				name: "2 Euro",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "EUR",
				value: 500,
				name: "5 Euro",
				type: "Note",
				active: true,
				color: "#97C2FC",
				image: null,
			},
			{
				code: "EUR",
				value: 1000,
				name: "10 Euro",
				type: "Note",
				active: true,
				color: "#E6A272",
				image: null,
			},
			{
				code: "EUR",
				value: 2000,
				name: "20 Euro",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "EUR",
				value: 5000,
				name: "50 Euro",
				type: "Note",
				active: true,
				color: "#FFA07A",
				image: null,
			},
			{
				code: "EUR",
				value: 10000,
				name: "100 Euro",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "EUR",
				value: 20000,
				name: "200 Euro",
				type: "Note",
				active: true,
				color: "#F4D03F",
				image: null,
			},
			{
				code: "EUR",
				value: 50000,
				name: "500 Euro",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	GBP: {
		code: "GBP",
		name: "British Pound",
		symbol: "\u00A3",
		isPrimary: false,
		decimalPlaces: 2,
		active: true,
		denominations: [
			{
				code: "GBP",
				value: 1,
				name: "1 Penny",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "GBP",
				value: 2,
				name: "2 Pence",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "GBP",
				value: 5,
				name: "5 Pence",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "GBP",
				value: 10,
				name: "10 Pence",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "GBP",
				value: 20,
				name: "20 Pence",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "GBP",
				value: 50,
				name: "50 Pence",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "GBP",
				value: 100,
				name: "1 Pound",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "GBP",
				value: 200,
				name: "2 Pound",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "GBP",
				value: 500,
				name: "5 Pound",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "GBP",
				value: 1000,
				name: "10 Pound",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "GBP",
				value: 2000,
				name: "20 Pound",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "GBP",
				value: 5000,
				name: "50 Pound",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
		],
	},
	JPY: {
		code: "JPY",
		name: "Japanese Yen",
		symbol: "\u00A5",
		isPrimary: false,
		decimalPlaces: 0,
		active: false,
		denominations: [
			{
				code: "JPY",
				value: 1,
				name: "1 Yen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "JPY",
				value: 5,
				name: "5 Yen",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "JPY",
				value: 10,
				name: "10 Yen",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "JPY",
				value: 50,
				name: "50 Yen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "JPY",
				value: 100,
				name: "100 Yen",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "JPY",
				value: 500,
				name: "500 Yen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "JPY",
				value: 1000,
				name: "1000 Yen",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "JPY",
				value: 2000,
				name: "2000 Yen",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "JPY",
				value: 5000,
				name: "5000 Yen",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "JPY",
				value: 10000,
				name: "10000 Yen",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
		],
	},
	CAD: {
		code: "CAD",
		name: "Canadian Dollar",
		symbol: "C$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "CAD",
				value: 5,
				name: "Nickel",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CAD",
				value: 10,
				name: "Dime",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CAD",
				value: 25,
				name: "Quarter",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CAD",
				value: 100,
				name: "Loonie",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CAD",
				value: 200,
				name: "Toonie",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CAD",
				value: 500,
				name: "Five Dollar",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "CAD",
				value: 1000,
				name: "Ten Dollar",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "CAD",
				value: 2000,
				name: "Twenty Dollar",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "CAD",
				value: 5000,
				name: "Fifty Dollar",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "CAD",
				value: 10000,
				name: "Hundred Dollar",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	AUD: {
		code: "AUD",
		name: "Australian Dollar",
		symbol: "A$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "AUD",
				value: 5,
				name: "5 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "AUD",
				value: 10,
				name: "10 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "AUD",
				value: 20,
				name: "20 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "AUD",
				value: 50,
				name: "50 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "AUD",
				value: 100,
				name: "1 Dollar",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "AUD",
				value: 200,
				name: "2 Dollar",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "AUD",
				value: 500,
				name: "5 Dollar",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "AUD",
				value: 1000,
				name: "10 Dollar",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "AUD",
				value: 2000,
				name: "20 Dollar",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "AUD",
				value: 5000,
				name: "50 Dollar",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "AUD",
				value: 10000,
				name: "100 Dollar",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	CHF: {
		code: "CHF",
		name: "Swiss Franc",
		symbol: "Fr",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "CHF",
				value: 5,
				name: "5 Rappen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 10,
				name: "10 Rappen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 20,
				name: "20 Rappen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 50,
				name: "50 Rappen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 100,
				name: "1 Franc",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 200,
				name: "2 Franc",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 500,
				name: "5 Franc",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CHF",
				value: 1000,
				name: "10 Franc",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "CHF",
				value: 2000,
				name: "20 Franc",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "CHF",
				value: 5000,
				name: "50 Franc",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "CHF",
				value: 10000,
				name: "100 Franc",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "CHF",
				value: 20000,
				name: "200 Franc",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "CHF",
				value: 100000,
				name: "1000 Franc",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	CNY: {
		code: "CNY",
		name: "Chinese Yuan",
		symbol: "\u00A5",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "CNY",
				value: 1,
				name: "1 Fen",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CNY",
				value: 2,
				name: "2 Fen",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CNY",
				value: 5,
				name: "5 Fen",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CNY",
				value: 10,
				name: "1 Jiao",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CNY",
				value: 50,
				name: "5 Jiao",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CNY",
				value: 100,
				name: "1 Yuan",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CNY",
				value: 100,
				name: "1 Yuan",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "CNY",
				value: 500,
				name: "5 Yuan",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "CNY",
				value: 1000,
				name: "10 Yuan",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "CNY",
				value: 2000,
				name: "20 Yuan",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "CNY",
				value: 5000,
				name: "50 Yuan",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "CNY",
				value: 10000,
				name: "100 Yuan",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	INR: {
		code: "INR",
		name: "Indian Rupee",
		symbol: "\u20B9",
		isPrimary: false,
		decimalPlaces: 2,
		active: true,
		denominations: [
			{
				code: "INR",
				value: 50,
				name: "50 Paise",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "INR",
				value: 100,
				name: "1 Rupee",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "INR",
				value: 200,
				name: "2 Rupees",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "INR",
				value: 500,
				name: "5 Rupees",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "INR",
				value: 1000,
				name: "10 Rupees",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "INR",
				value: 2000,
				name: "20 Rupees",
				type: "Note",
				active: true,
				color: "#FF6B6B",
				image: null,
			},
			{
				code: "INR",
				value: 5000,
				name: "50 Rupees",
				type: "Note",
				active: true,
				color: "#4ECDC4",
				image: null,
			},
			{
				code: "INR",
				value: 10000,
				name: "100 Rupees",
				type: "Note",
				active: true,
				color: "#45B7D1",
				image: null,
			},
			{
				code: "INR",
				value: 20000,
				name: "200 Rupees",
				type: "Note",
				active: true,
				color: "#F9CA24",
				image: null,
			},
			{
				code: "INR",
				value: 50000,
				name: "500 Rupees",
				type: "Note",
				active: true,
				color: "#6C5CE7",
				image: null,
			},
			{
				code: "INR",
				value: 200000,
				name: "2000 Rupees",
				type: "Note",
				active: true,
				color: "#A29BFE",
				image: null,
			},
		],
	},
	BRL: {
		code: "BRL",
		name: "Brazilian Real",
		symbol: "R$",
		isPrimary: false,
		decimalPlaces: 2,
		active: true,
		denominations: [
			{
				code: "BRL",
				value: 1,
				name: "1 Centavo",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "BRL",
				value: 5,
				name: "5 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "BRL",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "BRL",
				value: 25,
				name: "25 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "BRL",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "BRL",
				value: 100,
				name: "1 Real",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "BRL",
				value: 200,
				name: "2 Reais",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "BRL",
				value: 500,
				name: "5 Reais",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "BRL",
				value: 1000,
				name: "10 Reais",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "BRL",
				value: 2000,
				name: "20 Reais",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "BRL",
				value: 5000,
				name: "50 Reais",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "BRL",
				value: 10000,
				name: "100 Reais",
				type: "Note",
				active: true,
				color: "#F4D03F",
				image: null,
			},
			{
				code: "BRL",
				value: 20000,
				name: "200 Reais",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	KRW: {
		code: "KRW",
		name: "South Korean Won",
		symbol: "\u20A9",
		isPrimary: false,
		decimalPlaces: 0,
		active: true,
		denominations: [
			{
				code: "KRW",
				value: 1,
				name: "1 Won",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "KRW",
				value: 5,
				name: "5 Won",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "KRW",
				value: 10,
				name: "10 Won",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "KRW",
				value: 50,
				name: "50 Won",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "KRW",
				value: 100,
				name: "100 Won",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "KRW",
				value: 500,
				name: "500 Won",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "KRW",
				value: 1000,
				name: "1000 Won",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "KRW",
				value: 5000,
				name: "5000 Won",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "KRW",
				value: 10000,
				name: "10000 Won",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "KRW",
				value: 50000,
				name: "50000 Won",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
		],
	},
	MXN: {
		code: "MXN",
		name: "Mexican Peso",
		symbol: "$",
		isPrimary: false,
		decimalPlaces: 2,
		active: true,
		denominations: [
			{
				code: "MXN",
				value: 5,
				name: "5 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 20,
				name: "20 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 100,
				name: "1 Peso",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 200,
				name: "2 Pesos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 500,
				name: "5 Pesos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 1000,
				name: "10 Pesos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MXN",
				value: 2000,
				name: "20 Pesos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "MXN",
				value: 5000,
				name: "50 Pesos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "MXN",
				value: 10000,
				name: "100 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "MXN",
				value: 20000,
				name: "200 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "MXN",
				value: 50000,
				name: "500 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "MXN",
				value: 100000,
				name: "1000 Pesos",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	RUB: {
		code: "RUB",
		name: "Russian Ruble",
		symbol: "\u20BD",
		isPrimary: false,
		decimalPlaces: 2,
		active: true,
		denominations: [
			{
				code: "RUB",
				value: 1,
				name: "1 Kopeck",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "RUB",
				value: 5,
				name: "5 Kopecks",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "RUB",
				value: 10,
				name: "10 Kopecks",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "RUB",
				value: 50,
				name: "50 Kopecks",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "RUB",
				value: 100,
				name: "1 Ruble",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "RUB",
				value: 200,
				name: "2 Rubles",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "RUB",
				value: 500,
				name: "5 Rubles",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "RUB",
				value: 1000,
				name: "10 Rubles",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "RUB",
				value: 5000,
				name: "50 Rubles",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "RUB",
				value: 10000,
				name: "100 Rubles",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "RUB",
				value: 20000,
				name: "200 Rubles",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "RUB",
				value: 50000,
				name: "500 Rubles",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "RUB",
				value: 100000,
				name: "1000 Rubles",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "RUB",
				value: 200000,
				name: "2000 Rubles",
				type: "Note",
				active: true,
				color: "#F4D03F",
				image: null,
			},
			{
				code: "RUB",
				value: 500000,
				name: "5000 Rubles",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	HNL: {
		code: "HNL",
		name: "Honduran Lempira",
		symbol: "L",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "HNL",
				value: 5,
				name: "5 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HNL",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HNL",
				value: 20,
				name: "20 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HNL",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HNL",
				value: 100,
				name: "1 Lempira",
				type: "Note",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "HNL",
				value: 200,
				name: "2 Lempiras",
				type: "Note",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "HNL",
				value: 500,
				name: "5 Lempiras",
				type: "Note",
				active: true,
				color: "#85BB65",
				image: null,
			},
			{
				code: "HNL",
				value: 1000,
				name: "10 Lempiras",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "HNL",
				value: 2000,
				name: "20 Lempiras",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "HNL",
				value: 5000,
				name: "50 Lempiras",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "HNL",
				value: 10000,
				name: "100 Lempiras",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "HNL",
				value: 20000,
				name: "200 Lempiras",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "HNL",
				value: 50000,
				name: "500 Lempiras",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	SEK: {
		code: "SEK",
		name: "Swedish Krona",
		symbol: "kr",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "SEK",
				value: 50,
				name: "50 \u00D6re",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "SEK",
				value: 100,
				name: "1 Krona",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "SEK",
				value: 200,
				name: "2 Kronor",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "SEK",
				value: 500,
				name: "5 Kronor",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "SEK",
				value: 1000,
				name: "10 Kronor",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "SEK",
				value: 2000,
				name: "20 Kronor",
				type: "Note",
				active: true,
				color: "#FF6B6B",
				image: null,
			},
			{
				code: "SEK",
				value: 5000,
				name: "50 Kronor",
				type: "Note",
				active: true,
				color: "#4ECDC4",
				image: null,
			},
			{
				code: "SEK",
				value: 10000,
				name: "100 Kronor",
				type: "Note",
				active: true,
				color: "#45B7D1",
				image: null,
			},
			{
				code: "SEK",
				value: 20000,
				name: "200 Kronor",
				type: "Note",
				active: true,
				color: "#F9CA24",
				image: null,
			},
			{
				code: "SEK",
				value: 50000,
				name: "500 Kronor",
				type: "Note",
				active: true,
				color: "#6C5CE7",
				image: null,
			},
			{
				code: "SEK",
				value: 100000,
				name: "1000 Kronor",
				type: "Note",
				active: true,
				color: "#A29BFE",
				image: null,
			},
		],
	},
	NZD: {
		code: "NZD",
		name: "New Zealand Dollar",
		symbol: "NZ$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "NZD",
				value: 10,
				name: "10 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "NZD",
				value: 20,
				name: "20 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "NZD",
				value: 50,
				name: "50 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "NZD",
				value: 100,
				name: "1 Dollar",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "NZD",
				value: 200,
				name: "2 Dollar",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "NZD",
				value: 500,
				name: "5 Dollar",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "NZD",
				value: 1000,
				name: "10 Dollar",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "NZD",
				value: 2000,
				name: "20 Dollar",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "NZD",
				value: 5000,
				name: "50 Dollar",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "NZD",
				value: 10000,
				name: "100 Dollar",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	SGD: {
		code: "SGD",
		name: "Singapore Dollar",
		symbol: "S$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "SGD",
				value: 5,
				name: "5 Cent",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "SGD",
				value: 10,
				name: "10 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "SGD",
				value: 20,
				name: "20 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "SGD",
				value: 50,
				name: "50 Cent",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "SGD",
				value: 100,
				name: "1 Dollar",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "SGD",
				value: 200,
				name: "2 Dollar",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "SGD",
				value: 500,
				name: "5 Dollar",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "SGD",
				value: 1000,
				name: "10 Dollar",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "SGD",
				value: 5000,
				name: "50 Dollar",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "SGD",
				value: 10000,
				name: "100 Dollar",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "SGD",
				value: 100000,
				name: "1000 Dollar",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	THB: {
		code: "THB",
		name: "Thai Baht",
		symbol: "\u0E3F",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "THB",
				value: 25,
				name: "25 Satang",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "THB",
				value: 50,
				name: "50 Satang",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "THB",
				value: 100,
				name: "1 Baht",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "THB",
				value: 200,
				name: "2 Baht",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "THB",
				value: 500,
				name: "5 Baht",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "THB",
				value: 1000,
				name: "10 Baht",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "THB",
				value: 2000,
				name: "20 Baht",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "THB",
				value: 5000,
				name: "50 Baht",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "THB",
				value: 10000,
				name: "100 Baht",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "THB",
				value: 50000,
				name: "500 Baht",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "THB",
				value: 100000,
				name: "1000 Baht",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	NOK: {
		code: "NOK",
		name: "Norwegian Krone",
		symbol: "kr",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "NOK",
				value: 50,
				name: "50 \u00D8re",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "NOK",
				value: 100,
				name: "1 Krone",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "NOK",
				value: 500,
				name: "5 Kroner",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "NOK",
				value: 1000,
				name: "10 Kroner",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "NOK",
				value: 2000,
				name: "20 Kroner",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "NOK",
				value: 5000,
				name: "50 Kroner",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "NOK",
				value: 10000,
				name: "100 Kroner",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "NOK",
				value: 20000,
				name: "200 Kroner",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "NOK",
				value: 50000,
				name: "500 Kroner",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "NOK",
				value: 100000,
				name: "1000 Kroner",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	PLN: {
		code: "PLN",
		name: "Polish Z\u0142oty",
		symbol: "z\u0142",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "PLN",
				value: 1,
				name: "1 Grosz",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "PLN",
				value: 2,
				name: "2 Grosze",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "PLN",
				value: 5,
				name: "5 Groszy",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "PLN",
				value: 10,
				name: "10 Groszy",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PLN",
				value: 20,
				name: "20 Groszy",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PLN",
				value: 50,
				name: "50 Groszy",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PLN",
				value: 100,
				name: "1 Z\u0142oty",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "PLN",
				value: 200,
				name: "2 Z\u0142ote",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "PLN",
				value: 500,
				name: "5 Z\u0142otych",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "PLN",
				value: 1000,
				name: "10 Z\u0142otych",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "PLN",
				value: 2000,
				name: "20 Z\u0142otych",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "PLN",
				value: 5000,
				name: "50 Z\u0142otych",
				type: "Note",
				active: true,
				color: "#32CD32",
				image: null,
			},
			{
				code: "PLN",
				value: 10000,
				name: "100 Z\u0142otych",
				type: "Note",
				active: true,
				color: "#FFD700",
				image: null,
			},
			{
				code: "PLN",
				value: 20000,
				name: "200 Z\u0142otych",
				type: "Note",
				active: true,
				color: "#9370DB",
				image: null,
			},
			{
				code: "PLN",
				value: 50000,
				name: "500 Z\u0142otych",
				type: "Note",
				active: true,
				color: "#FF1493",
				image: null,
			},
		],
	},
	CZK: {
		code: "CZK",
		name: "Czech Koruna",
		symbol: "K\u010D",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "CZK",
				value: 50,
				name: "50 Hal\u00E9\u0159\u016F",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "CZK",
				value: 100,
				name: "1 Koruna",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CZK",
				value: 200,
				name: "2 Koruny",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CZK",
				value: 500,
				name: "5 Korun",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CZK",
				value: 1000,
				name: "10 Korun",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CZK",
				value: 2000,
				name: "20 Korun",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CZK",
				value: 5000,
				name: "50 Korun",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CZK",
				value: 10000,
				name: "100 Korun",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "CZK",
				value: 20000,
				name: "200 Korun",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "CZK",
				value: 50000,
				name: "500 Korun",
				type: "Note",
				active: true,
				color: "#32CD32",
				image: null,
			},
			{
				code: "CZK",
				value: 100000,
				name: "1000 Korun",
				type: "Note",
				active: true,
				color: "#FFD700",
				image: null,
			},
			{
				code: "CZK",
				value: 200000,
				name: "2000 Korun",
				type: "Note",
				active: true,
				color: "#9370DB",
				image: null,
			},
			{
				code: "CZK",
				value: 500000,
				name: "5000 Korun",
				type: "Note",
				active: true,
				color: "#FF1493",
				image: null,
			},
		],
	},
	HUF: {
		code: "HUF",
		name: "Hungarian Forint",
		symbol: "Ft",
		isPrimary: false,
		decimalPlaces: 0,
		active: false,
		denominations: [
			{
				code: "HUF",
				value: 5,
				name: "5 Forint",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "HUF",
				value: 10,
				name: "10 Forint",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "HUF",
				value: 20,
				name: "20 Forint",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HUF",
				value: 50,
				name: "50 Forint",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HUF",
				value: 100,
				name: "100 Forint",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "HUF",
				value: 200,
				name: "200 Forint",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "HUF",
				value: 500,
				name: "500 Forint",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "HUF",
				value: 1000,
				name: "1,000 Forint",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "HUF",
				value: 2000,
				name: "2,000 Forint",
				type: "Note",
				active: true,
				color: "#32CD32",
				image: null,
			},
			{
				code: "HUF",
				value: 5000,
				name: "5,000 Forint",
				type: "Note",
				active: true,
				color: "#FFD700",
				image: null,
			},
			{
				code: "HUF",
				value: 10000,
				name: "10,000 Forint",
				type: "Note",
				active: true,
				color: "#9370DB",
				image: null,
			},
			{
				code: "HUF",
				value: 20000,
				name: "20,000 Forint",
				type: "Note",
				active: true,
				color: "#FF1493",
				image: null,
			},
		],
	},
	DKK: {
		code: "DKK",
		name: "Danish Krone",
		symbol: "kr",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "DKK",
				value: 50,
				name: "50 \u00D8re",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "DKK",
				value: 100,
				name: "1 Krone",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "DKK",
				value: 200,
				name: "2 Kroner",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "DKK",
				value: 500,
				name: "5 Kroner",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "DKK",
				value: 1000,
				name: "10 Kroner",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "DKK",
				value: 2000,
				name: "20 Kroner",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "DKK",
				value: 5000,
				name: "50 Kroner",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "DKK",
				value: 10000,
				name: "100 Kroner",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "DKK",
				value: 20000,
				name: "200 Kroner",
				type: "Note",
				active: true,
				color: "#32CD32",
				image: null,
			},
			{
				code: "DKK",
				value: 50000,
				name: "500 Kroner",
				type: "Note",
				active: true,
				color: "#FFD700",
				image: null,
			},
			{
				code: "DKK",
				value: 100000,
				name: "1000 Kroner",
				type: "Note",
				active: true,
				color: "#9370DB",
				image: null,
			},
		],
	},
	DOP: {
		code: "DOP",
		name: "Dominican Peso",
		symbol: "RD$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "DOP",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "DOP",
				value: 25,
				name: "25 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "DOP",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "DOP",
				value: 1000,
				name: "10 Pesos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "DOP",
				value: 2000,
				name: "20 Pesos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "DOP",
				value: 5000,
				name: "50 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "DOP",
				value: 10000,
				name: "100 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "DOP",
				value: 20000,
				name: "200 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "DOP",
				value: 50000,
				name: "500 Pesos",
				type: "Note",
				active: true,
				color: "#F4D03F",
				image: null,
			},
			{
				code: "DOP",
				value: 100000,
				name: "1000 Pesos",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
			{
				code: "DOP",
				value: 200000,
				name: "2000 Pesos",
				type: "Note",
				active: true,
				color: "#DDA0DD",
				image: null,
			},
		],
	},
	UYU: {
		code: "UYU",
		name: "Uruguayan Peso",
		symbol: "$U",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "UYU",
				value: 1,
				name: "1 Centésimo",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "UYU",
				value: 5,
				name: "5 Centésimos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "UYU",
				value: 10,
				name: "10 Centésimos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "UYU",
				value: 50,
				name: "50 Centésimos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "UYU",
				value: 100,
				name: "1 Peso",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "UYU",
				value: 200,
				name: "2 Pesos",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "UYU",
				value: 500,
				name: "5 Pesos",
				type: "Coin",
				active: true,
				color: "#85BB65",
				image: null,
			},
			{
				code: "UYU",
				value: 1000,
				name: "10 Pesos",
				type: "Coin",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "UYU",
				value: 2000,
				name: "20 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "UYU",
				value: 5000,
				name: "50 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "UYU",
				value: 10000,
				name: "100 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "UYU",
				value: 20000,
				name: "200 Pesos",
				type: "Note",
				active: true,
				color: "#F4D03F",
				image: null,
			},
			{
				code: "UYU",
				value: 50000,
				name: "500 Pesos",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
			{
				code: "UYU",
				value: 100000,
				name: "1000 Pesos",
				type: "Note",
				active: true,
				color: "#9370DB",
				image: null,
			},
		],
	},
	ARS: {
		code: "ARS",
		name: "Argentine Peso",
		symbol: "$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "ARS",
				value: 1,
				name: "1 Centavo",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "ARS",
				value: 5,
				name: "5 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "ARS",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "ARS",
				value: 25,
				name: "25 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "ARS",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "ARS",
				value: 100,
				name: "1 Peso",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "ARS",
				value: 200,
				name: "2 Pesos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "ARS",
				value: 500,
				name: "5 Pesos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "ARS",
				value: 1000,
				name: "10 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "ARS",
				value: 2000,
				name: "20 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "ARS",
				value: 5000,
				name: "50 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	COP: {
		code: "COP",
		name: "Colombian Peso",
		symbol: "$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "COP",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "COP",
				value: 100,
				name: "100 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "COP",
				value: 200,
				name: "200 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "COP",
				value: 500,
				name: "500 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "COP",
				value: 1000,
				name: "1,000 Pesos",
				type: "Note",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "COP",
				value: 2000,
				name: "2,000 Pesos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "COP",
				value: 5000,
				name: "5,000 Pesos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "COP",
				value: 10000,
				name: "10,000 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "COP",
				value: 20000,
				name: "20,000 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "COP",
				value: 50000,
				name: "50,000 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "COP",
				value: 100000,
				name: "100,000 Pesos",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	CLP: {
		code: "CLP",
		name: "Chilean Peso",
		symbol: "$",
		isPrimary: false,
		decimalPlaces: 0,
		active: false,
		denominations: [
			{
				code: "CLP",
				value: 1,
				name: "1 Peso",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CLP",
				value: 5,
				name: "5 Pesos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "CLP",
				value: 10,
				name: "10 Pesos",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "CLP",
				value: 50,
				name: "50 Pesos",
				type: "Coin",
				active: false,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CLP",
				value: 100,
				name: "100 Pesos",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "CLP",
				value: 500,
				name: "500 Pesos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "CLP",
				value: 1000,
				name: "1,000 Pesos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "CLP",
				value: 2000,
				name: "2,000 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "CLP",
				value: 5000,
				name: "5,000 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "CLP",
				value: 10000,
				name: "10,000 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	PEN: {
		code: "PEN",
		name: "Peruvian Sol",
		symbol: "S/.",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "PEN",
				value: 1,
				name: "1 Centimo",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "PEN",
				value: 5,
				name: "5 Céntimos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "PEN",
				value: 10,
				name: "10 Céntimos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PEN",
				value: 20,
				name: "20 Céntimos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PEN",
				value: 50,
				name: "50 Céntimos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PEN",
				value: 100,
				name: "1 Sol",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "PEN",
				value: 200,
				name: "2 Soles",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "PEN",
				value: 500,
				name: "5 Soles",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "PEN",
				value: 1000,
				name: "10 Soles",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "PEN",
				value: 2000,
				name: "20 Soles",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
		],
	},
	BOB: {
		code: "BOB",
		name: "Bolivian Boliviano",
		symbol: "Bs",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "BOB",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "BOB",
				value: 20,
				name: "20 Centavos",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "BOB",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "BOB",
				value: 100,
				name: "1 Boliviano",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "BOB",
				value: 200,
				name: "2 Bolivianos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "BOB",
				value: 500,
				name: "5 Bolivianos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "BOB",
				value: 1000,
				name: "10 Bolivianos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
		],
	},
	PYG: {
		code: "PYG",
		name: "Paraguayan Guaraní",
		symbol: "₲",
		isPrimary: false,
		decimalPlaces: 0,
		active: false,
		denominations: [
			{
				code: "PYG",
				value: 50,
				name: "50 Guaraníes",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PYG",
				value: 100,
				name: "100 Guaraníes",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PYG",
				value: 500,
				name: "500 Guaraníes",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "PYG",
				value: 1000,
				name: "1,000 Guaraníes",
				type: "Note",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "PYG",
				value: 2000,
				name: "2,000 Guaraníes",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "PYG",
				value: 5000,
				name: "5,000 Guaraníes",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "PYG",
				value: 10000,
				name: "10,000 Guaraníes",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
		],
	},
	IDR: {
		code: "IDR",
		name: "Indonesian Rupiah",
		symbol: "Rp",
		isPrimary: false,
		decimalPlaces: 0,
		active: false,
		denominations: [
			{
				code: "IDR",
				value: 50,
				name: "50 Rupiah",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "IDR",
				value: 100,
				name: "100 Rupiah",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "IDR",
				value: 200,
				name: "200 Rupiah",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "IDR",
				value: 500,
				name: "500 Rupiah",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "IDR",
				value: 1000,
				name: "1,000 Rupiah",
				type: "Note",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "IDR",
				value: 2000,
				name: "2,000 Rupiah",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "IDR",
				value: 5000,
				name: "5,000 Rupiah",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "IDR",
				value: 10000,
				name: "10,000 Rupiah",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "IDR",
				value: 20000,
				name: "20,000 Rupiah",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "IDR",
				value: 50000,
				name: "50,000 Rupiah",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "IDR",
				value: 100000,
				name: "100,000 Rupiah",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
		],
	},
	PHP: {
		code: "PHP",
		name: "Philippine Peso",
		symbol: "₱",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "PHP",
				value: 1,
				name: "1 Centavo",
				type: "Coin",
				active: false,
				color: "#B87333",
				image: null,
			},
			{
				code: "PHP",
				value: 5,
				name: "5 Centavos",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "PHP",
				value: 10,
				name: "10 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PHP",
				value: 25,
				name: "25 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PHP",
				value: 50,
				name: "50 Centavos",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "PHP",
				value: 100,
				name: "1 Peso",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "PHP",
				value: 200,
				name: "2 Pesos",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "PHP",
				value: 500,
				name: "5 Pesos",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "PHP",
				value: 1000,
				name: "10 Pesos",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "PHP",
				value: 2000,
				name: "20 Pesos",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "PHP",
				value: 5000,
				name: "50 Pesos",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	VND: {
		code: "VND",
		name: "Vietnamese Dong",
		symbol: "₫",
		isPrimary: false,
		decimalPlaces: 0,
		active: false,
		denominations: [
			{
				code: "VND",
				value: 1000,
				name: "1,000 Đồng",
				type: "Note",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "VND",
				value: 2000,
				name: "2,000 Đồng",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "VND",
				value: 5000,
				name: "5,000 Đồng",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "VND",
				value: 10000,
				name: "10,000 Đồng",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "VND",
				value: 20000,
				name: "20,000 Đồng",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "VND",
				value: 50000,
				name: "50,000 Đồng",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
			{
				code: "VND",
				value: 100000,
				name: "100,000 Đồng",
				type: "Note",
				active: true,
				color: "#BB8FCE",
				image: null,
			},
			{
				code: "VND",
				value: 200000,
				name: "200,000 Đồng",
				type: "Note",
				active: true,
				color: "#9370DB",
				image: null,
			},
			{
				code: "VND",
				value: 500000,
				name: "500,000 Đồng",
				type: "Note",
				active: true,
				color: "#FF1493",
				image: null,
			},
		],
	},
	MYR: {
		code: "MYR",
		name: "Malaysian Ringgit",
		symbol: "RM",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "MYR",
				value: 5,
				name: "5 Sen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MYR",
				value: 10,
				name: "10 Sen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MYR",
				value: 20,
				name: "20 Sen",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "MYR",
				value: 50,
				name: "50 Sen",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "MYR",
				value: 100,
				name: "1 Ringgit",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "MYR",
				value: 200,
				name: "2 Ringgit",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "MYR",
				value: 500,
				name: "5 Ringgit",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "MYR",
				value: 1000,
				name: "10 Ringgit",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "MYR",
				value: 2000,
				name: "20 Ringgit",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
			{
				code: "MYR",
				value: 5000,
				name: "50 Ringgit",
				type: "Note",
				active: true,
				color: "#90EE90",
				image: null,
			},
		],
	},
	TWD: {
		code: "TWD",
		name: "New Taiwan Dollar",
		symbol: "NT$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "TWD",
				value: 1,
				name: "1 Cent",
				type: "Coin",
				active: false,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "TWD",
				value: 5,
				name: "5 Cents",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "TWD",
				value: 10,
				name: "10 Cents",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "TWD",
				value: 50,
				name: "50 Cents",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "TWD",
				value: 100,
				name: "1 Dollar",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "TWD",
				value: 200,
				name: "2 Dollars",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "TWD",
				value: 500,
				name: "5 Dollars",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "TWD",
				value: 1000,
				name: "10 Dollars",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "TWD",
				value: 2000,
				name: "20 Dollars",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
		],
	},
	ILS: {
		code: "ILS",
		name: "Israeli New Shekel",
		symbol: "₪",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "ILS",
				value: 10,
				name: "10 Agorot",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "ILS",
				value: 50,
				name: "50 Agorot",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "ILS",
				value: 100,
				name: "1 Shekel",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "ILS",
				value: 200,
				name: "2 Shekels",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "ILS",
				value: 2000,
				name: "20 Shekels",
				type: "Note",
				active: true,
				color: "#5F9EA0",
				image: null,
			},
			{
				code: "ILS",
				value: 5000,
				name: "50 Shekels",
				type: "Note",
				active: true,
				color: "#4682B4",
				image: null,
			},
			{
				code: "ILS",
				value: 10000,
				name: "100 Shekels",
				type: "Note",
				active: true,
				color: "#8B4513",
				image: null,
			},
			{
				code: "ILS",
				value: 20000,
				name: "200 Shekels",
				type: "Note",
				active: true,
				color: "#FF6347",
				image: null,
			},
		],
	},
	Default: {
		code: "USD",
		name: "US Dollar",
		symbol: "$",
		isPrimary: false,
		decimalPlaces: 2,
		active: false,
		denominations: [
			{
				code: "USD",
				value: 1,
				name: "Penny",
				type: "Coin",
				active: true,
				color: "#B87333",
				image: null,
			},
			{
				code: "USD",
				value: 5,
				name: "Nickel",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 10,
				name: "Dime",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 25,
				name: "Quarter",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 50,
				name: "Half Dollar",
				type: "Coin",
				active: true,
				color: "#A8A9AD",
				image: null,
			},
			{
				code: "USD",
				value: 100,
				name: "Dollar Coin",
				type: "Coin",
				active: true,
				color: "#D4AF37",
				image: null,
			},
			{
				code: "USD",
				value: 100,
				name: "One Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 200,
				name: "Two Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 500,
				name: "Five Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 1000,
				name: "Ten Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 2000,
				name: "Twenty Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 5000,
				name: "Fifty Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
			{
				code: "USD",
				value: 10000,
				name: "Hundred Dollar",
				type: "Note",
				active: true,
				color: "#85bb65",
				image: null,
			},
		],
	},
};
